type MessageLike = {
  id: string;
  content?: string | null;
  created_at?: string | null;
  timestamp?: string | null;
  edited?: boolean;
  updated_at?: string;
  edited_at?: string | null;
  deleted_at?: string | null;
  reactions?: unknown;
  attachments?: unknown;
  embeds?: unknown;
  components?: unknown;
  interaction_metadata?: unknown;
  flags?: number;
};

function normalizeTimestamp(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function normalizeReactions(reactions: unknown): Record<string, string[]> {
  if (!reactions) return {};
  if (Array.isArray(reactions)) {
    return reactions.reduce<Record<string, string[]>>((acc, reaction: any) => {
      if (reaction?.emoji) {
        acc[reaction.emoji] = Array.isArray(reaction.users) ? reaction.users : [];
      }
      return acc;
    }, {});
  }
  if (typeof reactions === 'object') {
    const out: Record<string, string[]> = {};
    Object.entries(reactions as Record<string, unknown>).forEach(([emoji, users]) => {
      out[emoji] = Array.isArray(users) ? users as string[] : [];
    });
    return out;
  }
  return {};
}

export function normalizeMessage<T extends MessageLike>(message: T): T {
  const createdAt =
    normalizeTimestamp(message.created_at)
    || normalizeTimestamp(message.timestamp)
    || normalizeTimestamp(message.updated_at)
    || new Date().toISOString();

  return {
    ...message,
    created_at: createdAt,
    updated_at: normalizeTimestamp(message.updated_at) || createdAt,
    reactions: normalizeReactions(message.reactions),
  } as T;
}

export function applyMessageUpdate<T extends MessageLike>(message: T, update: any): T {
  return {
    ...message,
    content: update.content ?? message.content,
    edited: update.edited ?? true,
    updated_at: update.updated_at ?? message.updated_at,
    edited_at: update.edited_at ?? update.updated_at ?? message.edited_at,
    attachments: update.attachments ?? message.attachments,
    embeds: update.embeds ?? message.embeds,
    components: update.components ?? message.components,
    interaction_metadata: update.interaction_metadata ?? message.interaction_metadata,
    flags: update.flags ?? message.flags,
  };
}

export function applyMessageDelete<T extends MessageLike>(message: T, deletion: any): T {
  return {
    ...message,
    deleted_at: deletion.deleted_at ?? new Date().toISOString(),
  };
}

export function applyReactionUpdate<T extends MessageLike>(message: T, update: any): T {
  if (update.reactions) {
    return { ...message, reactions: normalizeReactions(update.reactions) };
  }

  const emoji = update.emoji;
  const userId = update.userId || update.user_id;
  if (!emoji || !userId) return message;

  const reactions = normalizeReactions(message.reactions);
  const users = new Set(reactions[emoji] || []);
  if (update.action === 'remove') {
    users.delete(userId);
  } else {
    users.add(userId);
  }

  if (users.size === 0) {
    delete reactions[emoji];
  } else {
    reactions[emoji] = Array.from(users);
  }

  return { ...message, reactions };
}
